import { useModalState, csx } from '@vtex/admin-ui'
import React, {
  useCallback,
  useState,
  useEffect,
  useMemo,
  Fragment,
} from 'react'
import { getReduxStore } from '@/checkout-instore-legacy/redux/reduxStore'
import BottomModal from '@/checkout-instore-legacy/transaction/modal/BottomModal'

import FullModal from '@/checkout-instore-legacy/transaction/modal/FullModal'
import {
  cancelTransaction,
  clearPaymentApp,
} from '@/checkout-instore-legacy/transaction/redux/actions'
import { ExtensionPointError } from './ExtensionError'
import { ExtensionPoint } from './ExtensionPoint'
import PaymentAppError from './PaymentAppError'
import PaymentAppHeader from './header/PaymentAppHeader'
import PaymentAppCancel from './PaymentAppCancel'
import {
  debug as logDebug,
  error as logError,
  info as logInfo,
} from '@/checkout-instore-legacy/transaction/log/LogService'
import { LogEvent } from '@/checkout-instore-legacy/transaction/log/LogEvent'
import PubSub from '@/checkout-instore-legacy/utils/PubSub'
import { getOrderForm } from '@/checkout-instore-legacy/utils/orderFormUtils'
import OrderCancelModal from '@/checkout-instore-legacy/transaction/order/OrderCancelModal'
import {
  MetricAction,
  MetricShowcase,
} from '@/checkout-instore-legacy/VtexLakeHouse/typings'
import { sendMetric } from '@/checkout-instore-legacy/VtexLakeHouse'

interface CustomNodeJsGlobal extends NodeJS.Global {
  $?: any
}

declare const global: CustomNodeJsGlobal
const orderForm = getOrderForm()

const dismissPaymentApp = () => {
  getReduxStore().dispatch(cancelTransaction())
  getReduxStore().dispatch(clearPaymentApp())
  sendMetric({
    action: MetricAction.PaymentsCancelClick,
    showcase: MetricShowcase.Payments,
    cancellation_status: getReduxStore().getState().order.cancellationStatus,
    payment_methods: orderForm.paymentData.payments,
  })
}

export interface PaymentAppProps {
  appName: string
  appProps: Record<string, unknown>
}

const PaymentApp = ({ appName, appProps }: PaymentAppProps) => {
  const [error, setError] = useState<ExtensionPointError | undefined>()

  const paymentAppModalState = useModalState()
  const dismissDialogModalState = useModalState()
  const errorModalState = useModalState()

  // Workaround for modals work properly until admin-ui fixes it!
  // https://vtex.slack.com/archives/C01DVTFA4VA/p1698084391075899
  useEffect(() => {
    paymentAppModalState.setOpen(true)
    dismissDialogModalState.setOpen(false)
    errorModalState.setOpen(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => () => getReduxStore().dispatch(clearPaymentApp()), [])

  const handleAppDismissRequest = useCallback(() => {
    paymentAppModalState.setOpen(false)
    dismissDialogModalState.setOpen(true)
    errorModalState.setOpen(false)
    logInfo(LogEvent.SHOW_CANCEL_MODAL)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleRetry = useCallback(() => {
    setError(undefined)
    paymentAppModalState.setOpen(true)
    dismissDialogModalState.setOpen(false)
    errorModalState.setOpen(false)
    logInfo(LogEvent.RETRY_PAYMENT_APP)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleError = useCallback((error: ExtensionPointError) => {
    setError(error)
    paymentAppModalState.setOpen(false)
    dismissDialogModalState.setOpen(false)
    errorModalState.setOpen(true)
    logInfo(LogEvent.SHOW_PAYMENT_APP_ERROR_MODAL)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const extendedAppProps = useMemo(
    () => ({
      ...appProps,
      handleAppDismiss: handleAppDismissRequest,
      isSellerView: true,
      log: {
        debug: (event: LogEvent, extraData: Record<string, unknown> = {}) =>
          logDebug(event, { ...extraData, paymentInstance: appName }),
        error: (event: LogEvent, extraData: Record<string, unknown> = {}) =>
          logError(event, { ...extraData, paymentInstance: appName }),
        info: (event: LogEvent, extraData: Record<string, unknown> = {}) =>
          logInfo(event, { ...extraData, paymentInstance: appName }),
      },
    }),
    [appName, appProps, handleAppDismissRequest]
  )

  const handleGiveUp = useCallback(() => {
    const hasError = !!error
    paymentAppModalState.setOpen(!hasError)
    dismissDialogModalState.setOpen(false)
    errorModalState.setOpen(hasError)
  }, [dismissDialogModalState, error, errorModalState, paymentAppModalState])

  const cleanUpHook = useCallback(() => {
    delete global.$
  }, [])

  const afterLoadHook = useCallback(() => {
    const pubSub = new PubSub()
    pubSub.trigger('orderFormUpdated.vtex', getOrderForm())

    global.$ = () => pubSub
    global.$.ajax = pubSub.ajax
  }, [])

  return (
    <Fragment>
      <FullModal state={errorModalState}>
        <PaymentAppError
          onNewPayment={handleAppDismissRequest}
          onRetry={handleRetry}
        />
      </FullModal>

      <BottomModal
        state={dismissDialogModalState}
        hideOnClickOutside={false}
        hideOnEsc={false}
      >
        {error ? (
          <PaymentAppCancel
            onDismiss={dismissPaymentApp}
            onGiveUp={handleRetry}
          />
        ) : (
          <OrderCancelModal
            onCancel={dismissPaymentApp}
            onGiveUp={handleGiveUp}
          />
        )}
      </BottomModal>

      {!error && (
        <FullModal state={paymentAppModalState}>
          <PaymentAppHeader onDismiss={handleAppDismissRequest} />

          <div
            className={csx({
              '@tablet': {
                minWidth: '400px',
                minHeight: '150px',
              },
              height: 'calc(100% - 56px)',
            })}
          >
            <ExtensionPoint
              appName={appName}
              appProps={extendedAppProps}
              onError={handleError}
              afterLoadHook={afterLoadHook}
              cleanUpHook={cleanUpHook}
            />
          </div>
        </FullModal>
      )}
    </Fragment>
  )
}

export default PaymentApp
